import { createSubscriber, SvelteMap } from 'svelte/reactivity';
import { liveQuery } from 'dexie';

export function create_live_query<T>(
	fn: () => Promise<T>,
	on_emission?: (value: T | undefined, error: unknown) => void
) {
	let value: T | undefined = $state<T | undefined>(undefined);
	let error: unknown = $state<unknown>(undefined);
	let loading = $state(true);

	const subscribe = createSubscriber((update) => {
		const subscription = liveQuery(fn).subscribe({
			next: (v) => {
				value = v;
				error = undefined;
				loading = false;
				on_emission?.(v, undefined);
				update();
			},
			error: (e) => {
				error = e;
				loading = false;
				on_emission?.(undefined, e);
				update();
			}
		});
		return () => subscription.unsubscribe();
	});

	return {
		get value(): T | undefined {
			subscribe();
			return value;
		},
		get error(): unknown {
			subscribe();
			return error;
		},
		get loading(): boolean {
			subscribe();
			return loading;
		}
	};
}

export type ScopedView<T> = {
	values: T[];
	map: SvelteMap<string, T>;
	ready: boolean;
	error: unknown;
	promise: Promise<void> | null;
	last_rows: T[];
};

export function create_view<T extends { id: string }>(options: {
	query: () => Promise<T[]>;
	get_key: () => unknown;
}): ScopedView<T> {
	const map = new SvelteMap<string, T>();
	// Plain $state (not $derived): a derived created outside an effect becomes
	// owned by whichever effect first reads it, and goes inert (stale values +
	// derived_inert warning) when that effect re-runs — e.g. paginating a
	// table re-renders the component and its effect, freezing the rows.
	let values = $state<T[]>([]);
	let ready = $state(false);
	let error: unknown = $state<unknown>(undefined);
	let promise: Promise<void> | null = $state<Promise<void> | null>(null);
	let last_rows: T[] = [];

	const subscribe = createSubscriber((update) => {
		void update;
		return $effect.root(() => {
			// re-subscribes when the key state changes (tracked via get_key);
			// the teardown of each run unsubscribes the previous live query
			$effect(() => {
				void options.get_key();
				const deferred = Promise.withResolvers<void>();
				promise = deferred.promise;
				ready = false;
				const subscription = liveQuery(options.query).subscribe({
					next: (rows) => {
						map.clear();
						for (const row of rows) map.set(row.id, row);
						values = Array.from(map.values());
						last_rows = rows;
						error = undefined;
						ready = true;
						promise = null;
						deferred.resolve();
					},
					error: (e) => {
						error = e;
						ready = true;
						promise = null;
						deferred.resolve();
					}
				});
				return () => subscription.unsubscribe();
			});
		});
	});

	function ensure() {
		subscribe();
	}

	return {
		get values(): T[] {
			ensure();
			return values;
		},
		get map(): SvelteMap<string, T> {
			ensure();
			return map;
		},
		get ready(): boolean {
			ensure();
			return ready;
		},
		get error(): unknown {
			ensure();
			return error;
		},
		get promise(): Promise<void> | null {
			ensure();
			return promise;
		},
		get last_rows(): T[] {
			return last_rows;
		}
	};
}

export function mount_view_for_tests<T>(view: { get values(): T }): () => void {
	return $effect.root(() => {
		$effect(() => {
			void view.values;
		});
	});
}

export function create_test_signal<T>(initial: T) {
	let value = $state<T>(initial);
	return {
		get value(): T {
			return value;
		},
		set value(next: T) {
			value = next;
		}
	};
}
